/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any */

import { ProductType, SubLevel } from '../types';
import { Upsell } from '../components';
import { Page, PageProps } from './shared/Page';
import { ReactNode } from 'react';
import { isBloomberg } from '../util';
import useAuth from 'hooks/auth/useAuth';

type AccessProtectedPageProps = {
  productType: ProductType;
  renderContent: () => ReactNode;
  overrideCheck?: boolean;
  wrapperSx?: any;
  pageProps?: Partial<PageProps>;
};

const TYPES_TO_IMGS: Map<ProductType, string> = new Map([
  [ProductType.HOME, 'home-locked'],
  [ProductType.INDICES, 'indices-locked'],
  [ProductType.IMPLIED_VOL, 'implied-vol-locked'],
  [ProductType.TRACE, 'trace-locked'],
  [ProductType.INTEGRATIONS, 'integrations-locked'],
  [ProductType.FOUNDERS_NOTES, 'founders-note-locked'],
]);

export const AccessProtectedPage = ({
  productType,
  renderContent,
  wrapperSx,
  pageProps,
  overrideCheck = false,
}: AccessProtectedPageProps) => {
  const { hasAccessToProduct } = useAuth();
  const hasAccess = hasAccessToProduct(productType);

  if (!isBloomberg() && !overrideCheck && !hasAccess) {
    const requiredLevel = () => {
      switch (productType) {
        case ProductType.IMPLIED_VOL:
        case ProductType.HIRO:
        case ProductType.TRACE:
        case ProductType.TAPE:
        case ProductType.MOVIEFONE:
          return SubLevel.ALPHA;
        case ProductType.EQUITYHUB:
          return SubLevel.PRO;
        default:
          return SubLevel.ESSENTIAL;
      }
    };

    const level = requiredLevel();
    let levelStr = 'Essential';
    if (level === SubLevel.ALPHA) {
      levelStr = 'Alpha';
    } else if (level === SubLevel.PRO) {
      levelStr = 'Pro';
    }

    let items: string[];

    if (level === SubLevel.ESSENTIAL) {
      items = [
        'Unique support and resistance levels for 3,500+ US tickers',
        'Expert market commentary available each morning',
        'Real-time options order flow data',
        'Powerful tools built to support your trading',
      ];
    } else {
      items = [
        'Key levels for 3,500+ stocks & indices',
        'Proprietary & assumption-free data on market maker behavior',
        'Highly actionable alerts for options and equity traders',
      ];

      if (level >= SubLevel.ALPHA) {
        items = ['Real-time charts incorporating all options trades'].concat(
          items,
        );
      }
    }

    const subtitle = `With a SpotGamma ${levelStr} subscription, you'll get:`;
    const title = `Unlock Access with SpotGamma ${levelStr}`;

    return (
      <Upsell
        title={title}
        subtitle={subtitle}
        items={items}
        imageUrl={TYPES_TO_IMGS.get(productType) ?? ''}
      />
    );
  }

  return (
    <Page {...(pageProps ?? {})} sx={wrapperSx}>
      {renderContent()}
    </Page>
  );
};
